import { motion } from "framer-motion";

// Decorative geometric shapes and lines
export const DiagonalLines = ({ className = "" }: { className?: string }) => (
  <svg 
    className={`absolute pointer-events-none ${className}`}
    width="100%" 
    height="100%" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <pattern id="diagonal-lines" patternUnits="userSpaceOnUse" width="40" height="40" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="40" stroke="currentColor" strokeWidth="1" opacity="0.1" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#diagonal-lines)" />
  </svg>
);

export const GridPattern = ({ className = "" }: { className?: string }) => (
  <svg 
    className={`absolute pointer-events-none ${className}`}
    width="100%" 
    height="100%" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <pattern id="grid-pattern" patternUnits="userSpaceOnUse" width="80" height="80">
        <path d="M 80 0 L 0 0 0 80" fill="none" stroke="currentColor" strokeWidth="0.3" opacity="0.06" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#grid-pattern)" />
  </svg>
);

export const DotPattern = ({ className = "" }: { className?: string }) => (
  <svg 
    className={`absolute pointer-events-none ${className}`}
    width="100%" 
    height="100%" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <pattern id="dot-pattern" patternUnits="userSpaceOnUse" width="24" height="24">
        <circle cx="2" cy="2" r="1" fill="currentColor" opacity="0.2" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#dot-pattern)" />
  </svg>
);

export const FloatingOrb = ({ 
  className = "", 
  color = "brand",
  size = "lg",
  delay = 0 
}: { 
  className?: string;
  color?: "brand" | "accent" | "gold";
  size?: "sm" | "md" | "lg" | "xl";
  delay?: number;
}) => {
  const sizeClasses = {
    sm: "w-32 h-32",
    md: "w-48 h-48",
    lg: "w-64 h-64",
    xl: "w-96 h-96"
  };
  
  const colorClasses = {
    brand: "from-brand/30 via-brand/10 to-transparent",
    accent: "from-accent/30 via-accent/10 to-transparent",
    gold: "from-amber-500/30 via-amber-400/10 to-transparent"
  };

  return (
    <motion.div
      className={`absolute rounded-full bg-gradient-radial ${sizeClasses[size]} ${colorClasses[color]} blur-3xl ${className}`}
      animate={{
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.5, 0.3],
        x: [-20, 20, -20],
        y: [-10, 10, -10]
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut",
        delay
      }}
    />
  );
};

export const GeometricAccent = ({ className = "" }: { className?: string }) => (
  <motion.div 
    className={`absolute ${className}`}
    initial={{ opacity: 0, scale: 0 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.6, ease: "easeOut" }}
  >
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <motion.path
        d="M60 10L110 35V85L60 110L10 85V35L60 10Z"
        stroke="currentColor"
        strokeWidth="1"
        fill="none"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2, ease: "easeInOut" }}
        className="text-brand/30"
      />
      <motion.circle
        cx="60"
        cy="60"
        r="25"
        stroke="currentColor"
        strokeWidth="1"
        fill="none"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.5, delay: 0.5, ease: "easeInOut" }}
        className="text-brand/50"
      />
    </svg>
  </motion.div>
);

export const AnimatedLine = ({ 
  orientation = "horizontal",
  className = "" 
}: { 
  orientation?: "horizontal" | "vertical";
  className?: string;
}) => (
  <motion.div
    className={`bg-gradient-to-r from-transparent via-brand/50 to-transparent ${
      orientation === "horizontal" ? "h-px w-full" : "w-px h-full"
    } ${className}`}
    initial={{ scaleX: orientation === "horizontal" ? 0 : 1, scaleY: orientation === "vertical" ? 0 : 1 }}
    whileInView={{ scaleX: 1, scaleY: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 1, ease: "easeOut" }}
  />
);

export const CornerAccent = ({ 
  position = "top-left",
  className = "" 
}: { 
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  className?: string;
}) => {
  const positionClasses = {
    "top-left": "top-0 left-0",
    "top-right": "top-0 right-0 rotate-90",
    "bottom-left": "bottom-0 left-0 -rotate-90",
    "bottom-right": "bottom-0 right-0 rotate-180"
  };

  return (
    <motion.div 
      className={`absolute ${positionClasses[position]} ${className}`}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 0V60" stroke="currentColor" strokeWidth="1" className="text-brand/40" />
        <path d="M0 0H60" stroke="currentColor" strokeWidth="1" className="text-brand/40" />
        <circle cx="0" cy="0" r="4" fill="currentColor" className="text-brand/60" />
      </svg>
    </motion.div>
  );
};

export const GlowingBorder = ({ className = "" }: { className?: string }) => (
  <motion.div
    className={`absolute inset-0 rounded-2xl ${className}`}
    style={{
      background: "linear-gradient(90deg, transparent, hsl(var(--brand) / 0.3), transparent)",
      backgroundSize: "200% 100%"
    }}
    animate={{
      backgroundPosition: ["200% 0%", "-200% 0%"]
    }}
    transition={{
      duration: 3,
      repeat: Infinity,
      ease: "linear"
    }}
  />
);

export const FloatingParticle = ({ 
  className = "",
  delay = 0 
}: { 
  className?: string;
  delay?: number;
}) => (
  <motion.div
    className={`absolute w-1 h-1 rounded-full bg-brand/60 ${className}`}
    animate={{
      y: [-20, -100],
      opacity: [0, 1, 0],
      scale: [0, 1, 0.5]
    }}
    transition={{
      duration: 3,
      repeat: Infinity,
      ease: "easeOut",
      delay
    }}
  />
);

export const PremiumBadge = ({ 
  children,
  className = "" 
}: { 
  children: React.ReactNode;
  className?: string;
}) => (
  <motion.div
    className={`relative inline-flex items-center gap-2 px-4 py-2 ${className}`}
    whileHover={{ scale: 1.02 }}
  >
    {/* Glassmorphism background */}
    <div className="absolute inset-0 bg-white/10 backdrop-blur-xl rounded-full border border-white/20" />
    
    {/* Shimmer effect */}
    <motion.div
      className="absolute inset-0 rounded-full overflow-hidden"
      initial={{ opacity: 0 }}
      whileHover={{ opacity: 1 }}
    >
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
        animate={{ x: ["-100%", "100%"] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
      />
    </motion.div>
    
    <span className="relative z-10">{children}</span>
  </motion.div>
);

export const SectionDivider = ({ className = "" }: { className?: string }) => (
  <div className={`relative w-full py-12 ${className}`}>
    <div className="absolute left-0 right-0 top-1/2 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
    <motion.div
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rotate-45 border border-brand/50 bg-background"
      animate={{ rotate: [45, 225, 45] }}
      transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
    />
  </div>
);
